import type {
  ActiveEventSnapshot,
  AudioCue,
  GameState,
  SessionSummary,
} from "../domain/types";

export interface GameBusEvents {
  state: { state: GameState; active: ActiveEventSnapshot[] };
  audio: { cue: AudioCue; progress?: number };
  visual: { cue: string; critical?: boolean };
  feedback: { product: string; message: string };
  ended: SessionSummary;
}

type Handler<T> = (payload: T) => void;

export class TypedGameBus {
  private readonly listeners = new Map<
    keyof GameBusEvents,
    Set<Handler<never>>
  >();

  on<K extends keyof GameBusEvents>(
    event: K,
    handler: Handler<GameBusEvents[K]>,
  ): () => void {
    const handlers = this.listeners.get(event) ?? new Set<Handler<never>>();
    handlers.add(handler as Handler<never>);
    this.listeners.set(event, handlers);
    return () => handlers.delete(handler as Handler<never>);
  }

  emit<K extends keyof GameBusEvents>(
    event: K,
    payload: GameBusEvents[K],
  ): void {
    this.listeners.get(event)?.forEach((handler) => handler(payload as never));
  }

  clear(): void {
    this.listeners.clear();
  }
}
